import type { Router } from './routers';
import { networkOfRouter, routerNetworkLabel } from './routerNetwork';

/**
 * The fields the router editor shows and compares, and what to call them.
 * A field listed here is a field the confirmation can explain, so nothing
 * changes silently and no dialog shows a raw key.
 */
export const FIELD_LABELS: Partial<Record<keyof Router, string>> = {
  id: 'Router id',
  name: 'Name',
  rsp: 'RSP',
  mode: 'Mode',
  network: 'Network',
  cost: 'Cost',
  description1: 'Description 1',
  description2: 'Description 2',
  description3: 'Description 3',
  published: 'Published',
};

export interface Change {
  field: keyof Router;
  from: string;
  to: string;
}

/** A value as a person reads it in the confirmation. */
export const formatValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  const text = String(value ?? '').trim();
  return text === '' ? '—' : text;
};

const valueOf = (router: Router, field: keyof Router): string => (
  field === 'network'
    ? routerNetworkLabel(networkOfRouter(router))
    : formatValue(router[field])
);

/** What changed between the stored router and the one being edited. */
export const diffRouters = (before: Router, after: Router): Change[] => (
  (Object.keys(FIELD_LABELS) as (keyof Router)[])
    .filter((f) => valueOf(before, f) !== valueOf(after, f))
    .map((f) => ({ field: f, from: valueOf(before, f), to: valueOf(after, f) }))
);

/**
 * What must be true before a router can be saved. The id is only required
 * on a new one: an existing router is addressed by the id it already has.
 */
export const validateRouter = (router: Router, isNew: boolean): string[] => {
  const errors: string[] = [];
  if (isNew && router.id.trim() === '') errors.push('Router id is required');
  if (router.name.trim() === '') errors.push('Name is required');
  const cost = router.cost.trim();
  if (cost === '' || Number.isNaN(Number(cost))) {
    errors.push('Cost must be a number');
  } else if (Number(cost) < 0) {
    errors.push('Cost must be 0 or more');
  }
  return errors;
};
